// ─────────────────────────────────────────────────────────────────────────
// usePushNotifications.ts
// ─────────────────────────────────────────────────────────────────────────
// Wraps the browser Push API (PushManager) + our /api/push_subscribe.php
// endpoint so both AdminDashboard (broadcast channel) and
// OnlineJobSheetPage (per-job driver channel via ?token=) can opt in with
// one function call.
//
// Usage (admin):
//   const { permission, status, subscribe } = usePushNotifications();
//   <button onClick={subscribe}>เปิดการแจ้งเตือน</button>
//
// Usage (driver — pass the job's driverToken so the subscription is scoped
// to that job's push channel):
//   const { subscribe } = usePushNotifications(job?.driverToken ?? null);
// ─────────────────────────────────────────────────────────────────────────

import { useCallback, useEffect, useState } from 'react';
import { pushApi } from './api';

export type PushStatus = 'idle' | 'subscribing' | 'subscribed' | 'error';

export interface UsePushNotificationsResult {
  /** Browser Notification permission state, or 'unsupported' if the Push API isn't available at all. */
  permission: NotificationPermission | 'unsupported';
  status: PushStatus;
  /** Requests permission (if needed) and registers the push subscription with the server. */
  subscribe: () => Promise<void>;
}

function urlBase64ToUint8Array(base64String: string): Uint8Array<ArrayBuffer> {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(new ArrayBuffer(rawData.length));
  for (let i = 0; i < rawData.length; i++) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

/**
 * @param driverToken  Pass the job's driverToken to subscribe as a driver
 *   (scoped to that specific job's push channel). Leave undefined/null for
 *   admin usage — the request will carry the admin's session cookie instead.
 */
export function usePushNotifications(driverToken?: string | null): UsePushNotificationsResult {
  const [permission, setPermission] = useState<NotificationPermission | 'unsupported'>('default');
  const [status, setStatus] = useState<PushStatus>('idle');

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (!('Notification' in window) || !('serviceWorker' in navigator) || !('PushManager' in window)) {
      setPermission('unsupported');
      return;
    }
    setPermission(Notification.permission);
  }, []);

  const subscribe = useCallback(async () => {
    if (permission === 'unsupported') return;
    setStatus('subscribing');
    try {
      const perm = await Notification.requestPermission();
      setPermission(perm);
      if (perm !== 'granted') {
        setStatus('idle');
        return;
      }

      const registration = await navigator.serviceWorker.ready;
      let sub = await registration.pushManager.getSubscription();

      if (!sub) {
        const { publicKey } = await pushApi.getVapidPublicKey();
        sub = await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(publicKey),
        });
      }

      const json = sub.toJSON() as { endpoint?: string; keys?: { p256dh?: string; auth?: string } };
      if (!json.endpoint || !json.keys?.p256dh || !json.keys?.auth) {
        throw new Error('invalid subscription payload');
      }

      await pushApi.subscribe(
        { endpoint: json.endpoint, keys: { p256dh: json.keys.p256dh, auth: json.keys.auth } },
        driverToken ?? undefined,
      );
      setStatus('subscribed');
    } catch {
      setStatus('error');
    }
  }, [permission, driverToken]);

  return { permission, status, subscribe };
}
